import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, ShoppingBag, Eye, Heart, Package, Star, 
  TrendingUp, Clock, ChevronRight, BarChart3, 
  MessageSquare, Mail, RefreshCw, DollarSign, Activity,
  Image, Film, Edit, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { products } from '../data/mockData';
import { getImageUrl } from '../lib/utils';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area
} from 'recharts';
import StoryManager from '../components/admin/StoryManager';
import ProductManager from '../components/admin/ProductManager';
import BlogManager from '../components/admin/BlogManager';
import UnifiedImageManager from '../components/admin/UnifiedImageManager';
import CollectionManager from '../components/admin/CollectionManager';

// ===================== ActivityFeedTab =====================
// Shows a real-time stream of every event happening on the site.
const ACTIVITY_LABELS = {
  page_view: { tr: 'Sayfa görüntüledi', color: 'bg-blue-100 text-blue-700' },
  page_exit: { tr: 'Sayfadan çıktı', color: 'bg-gray-100 text-gray-700' },
  product_view: { tr: 'Ürün inceledi', color: 'bg-purple-100 text-purple-700' },
  product_click: { tr: 'Ürüne tıkladı', color: 'bg-indigo-100 text-indigo-700' },
  add_to_cart: { tr: 'Sepete ekledi', color: 'bg-green-100 text-green-700' },
  remove_from_cart: { tr: 'Sepetten çıkardı', color: 'bg-red-100 text-red-700' },
  add_to_favorites: { tr: 'Favoriye ekledi', color: 'bg-pink-100 text-pink-700' },
  remove_from_favorites: { tr: 'Favoriden çıkardı', color: 'bg-rose-100 text-rose-700' },
  search: { tr: 'Arama yaptı', color: 'bg-yellow-100 text-yellow-700' },
  filter: { tr: 'Filtre uyguladı', color: 'bg-amber-100 text-amber-700' },
  filter_used: { tr: 'Filtre kullandı', color: 'bg-amber-100 text-amber-700' },
  user_login: { tr: 'Giriş yaptı', color: 'bg-emerald-100 text-emerald-700' },
  user_logout: { tr: 'Çıkış yaptı', color: 'bg-slate-100 text-slate-700' },
  user_register: { tr: 'Kayıt oldu', color: 'bg-teal-100 text-teal-700' },
};

const ActivityFeedTab = ({ backendUrl, users, setSelectedUser, formatDate }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [productMap, setProductMap] = useState({});

  // Build a quick user_id -> user lookup
  const userMap = React.useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.user_id] = u; });
    return m;
  }, [users]);

  // Build a product map for product-name lookups
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendUrl}/api/products`);
        if (res.ok) {
          const list = await res.json();
          if (!cancelled) {
            const m = {};
            list.forEach((p) => { m[p.id] = p; });
            setProductMap(m);
          }
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [backendUrl]);

  const fetchActivity = React.useCallback(async () => {
    try {
      const res = await fetch(`${backendUrl}/api/admin/analytics/user-activity?limit=200`);
      if (res.ok) {
        const data = await res.json();
        setEvents(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('activity fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchActivity();
  }, [fetchActivity]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(fetchActivity, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchActivity]);

  const filtered = filterType === 'all' ? events : events.filter((e) => e.event_type === filterType);

  return (
    <div className="p-6" data-testid="activity-feed-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="text-lg font-medium flex items-center gap-2">
          <Eye size={20} className="text-indigo-500" />
          Canlı Kullanıcı Aktivitesi ({filtered.length})
          {autoRefresh && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 ml-2">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
              CANLI
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <select
            data-testid="activity-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="text-sm border rounded px-2 py-1"
          >
            <option value="all">Tüm hareketler</option>
            {Object.entries(ACTIVITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v.tr}</option>
            ))}
          </select>
          <button
            data-testid="activity-autorefresh-btn"
            onClick={() => setAutoRefresh((v) => !v)}
            className={`text-xs px-3 py-1 rounded border ${autoRefresh ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
          >
            {autoRefresh ? 'Otomatik yenile: AÇIK' : 'Otomatik yenile: KAPALI'}
          </button>
          <button
            data-testid="activity-refresh-btn"
            onClick={fetchActivity}
            className="text-xs px-3 py-1 rounded border bg-white hover:bg-gray-50"
          >
            <RefreshCw size={12} className="inline mr-1" />
            Yenile
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Yükleniyor...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">Bu filtre için hareket bulunamadı</div>
      ) : (
        <div className="bg-white border rounded-lg divide-y max-h-[600px] overflow-y-auto">
          {filtered.map((ev, idx) => {
            const info = ACTIVITY_LABELS[ev.event_type] || { tr: ev.event_type, color: 'bg-gray-100 text-gray-700' };
            const u = ev.user_id ? userMap[ev.user_id] : null;
            const product = ev.product_id ? productMap[ev.product_id] : null;
            return (
              <div key={ev.id || idx} className="flex items-start gap-3 px-4 py-3 text-xs hover:bg-gray-50">
                <span className={`shrink-0 px-2 py-0.5 rounded font-medium ${info.color}`}>
                  {info.tr}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-700 truncate">
                    {u ? (
                      <button
                        data-testid={`activity-user-link-${u.user_id}`}
                        onClick={() => setSelectedUser(u)}
                        className="font-medium text-black hover:underline"
                      >
                        {u.name || u.email}
                      </button>
                    ) : (
                      <span className="text-gray-500">
                        Misafir <code className="bg-gray-100 px-1 rounded">{(ev.visitor_id || 'anonim').slice(-8)}</code>
                      </span>
                    )}
                    {ev.page && (
                      <>
                        <span className="text-gray-400"> → </span>
                        <code className="bg-gray-100 px-1 rounded">{ev.page}</code>
                      </>
                    )}
                  </p>
                  {product && (
                    <p className="text-gray-600 truncate mt-0.5">
                      <span className="text-gray-400">Ürün:</span> {product.name}
                    </p>
                  )}
                  {ev.search_query && (
                    <p className="text-gray-600 truncate mt-0.5">
                      <span className="text-gray-400">Arama:</span> "{ev.search_query}"
                    </p>
                  )}
                  {ev.filter_type && (
                    <p className="text-gray-600 truncate mt-0.5">
                      <span className="text-gray-400">{ev.filter_type}:</span> {ev.filter_value}
                    </p>
                  )}
                  {(ev.duration != null || ev.scroll_depth != null) && (
                    <p className="text-gray-500 mt-0.5">
                      {ev.duration != null && <>Süre: {Math.round((ev.duration || 0) / 1000)}sn </>}
                      {ev.scroll_depth != null && <>· Kaydırma: %{ev.scroll_depth}</>}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-gray-400 text-[10px] whitespace-nowrap">
                  {formatDate(ev.created_at)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// User Detail Modal Component
const UserDetailModal = ({ user, onClose, backendUrl }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // Fetch user details
        const userRes = await fetch(`${backendUrl}/api/admin/users/${user.user_id}`);
        if (userRes.ok) {
          const userData = await userRes.json();
          setUserDetails(userData);
        }

        // Fetch products for showing favorites
        const productsRes = await fetch(`${backendUrl}/api/products`);
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [user.user_id, backendUrl]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get product by ID
  const getProduct = (productId) => products.find(p => p.id === productId);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-lg font-medium">Kullanıcı Detayları</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-500">Yükleniyor...</p>
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* User Header */}
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-20 h-20 rounded-full object-cover" />
              ) : (
                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center">
                  <Users size={32} className="text-gray-500" />
                </div>
              )}
              <div>
                <h4 className="text-xl font-medium">{user.name}</h4>
                <p className="text-gray-500">{user.email}</p>
                <span className={`inline-block mt-2 px-3 py-1 text-sm rounded ${
                  user.auth_type === 'google' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                }`}>
                  {user.auth_type === 'google' ? 'Google ile kayıt' : 'E-posta ile kayıt'}
                </span>
              </div>
            </div>
            
            {/* Contact & Address Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Contact Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2 text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  İletişim Bilgileri
                </h5>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Telefon</p>
                    <p className="font-medium">{user.phone || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs">E-posta</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                </div>
              </div>
              
              {/* Address Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2 text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Adres Bilgileri
                </h5>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-gray-500 text-xs">Açık Adres</p>
                    <p className="font-medium">{user.address || '-'}</p>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <p className="text-gray-500 text-xs">İlçe</p>
                      <p className="font-medium">{user.district || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">İl</p>
                      <p className="font-medium">{user.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs">Posta Kodu</p>
                      <p className="font-medium">{user.postal_code || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Activity Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-red-50 rounded-lg p-3 text-center">
                <Heart size={20} className="mx-auto text-red-500 mb-1" />
                <p className="text-xl font-bold text-red-600">{user.favorites_count || 0}</p>
                <p className="text-xs text-red-500">Favori</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <Package size={20} className="mx-auto text-blue-500 mb-1" />
                <p className="text-xl font-bold text-blue-600">{user.orders_count || 0}</p>
                <p className="text-xs text-blue-500">Sipariş</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-3 text-center">
                <Eye size={20} className="mx-auto text-purple-500 mb-1" />
                <p className="text-xl font-bold text-purple-600">{user.total_events || 0}</p>
                <p className="text-xs text-purple-500">Aktivite</p>
              </div>
            </div>

            {/* Favorite Products */}
            {userDetails?.favorites && userDetails.favorites.length > 0 && (
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <Heart size={18} className="text-red-500" />
                  Favori Ürünler ({userDetails.favorites.length})
                </h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {userDetails.favorites.slice(0, 8).map((fav, idx) => {
                    const product = getProduct(fav.product_id);
                    return product ? (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                        <img 
                          src={getImageUrl(product.image)} 
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded"
                          onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/48'; }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{product.name}</p>
                          <p className="text-xs text-gray-500">{product.prices?.['65x65'] || product.price} ₺</p>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Recent Product Views */}
            {userDetails?.product_views && userDetails.product_views.length > 0 && (
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <Eye size={18} className="text-purple-500" />
                  Son Görüntülenen Ürünler ({userDetails.product_views.length})
                </h5>
                <div className="space-y-2">
                  {userDetails.product_views.slice(0, 5).map((view, idx) => {
                    const product = getProduct(view.product_id);
                    return product ? (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                        <img 
                          src={getImageUrl(product.image)} 
                          alt={product.name}
                          className="w-14 h-14 object-cover rounded"
                          onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/56'; }}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-gray-500">
                            {formatDate(view.created_at)} • {Math.round((view.duration || 0) / 1000)}sn görüntülendi
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium">{product.prices?.['65x65'] || product.price} ₺</p>
                        </div>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {/* Recent Orders */}
            {userDetails?.orders && userDetails.orders.length > 0 && (
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <Package size={18} className="text-blue-500" />
                  Siparişler ({userDetails.orders.length})
                </h5>
                <div className="space-y-2">
                  {userDetails.orders.slice(0, 5).map((order, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div>
                        <p className="font-medium text-sm">#{order.order_id?.slice(-8) || idx}</p>
                        <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{order.total_amount} ₺</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {order.status === 'completed' ? 'Tamamlandı' : 
                           order.status === 'pending' ? 'Bekliyor' : order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed Activity Timeline */}
            {userDetails?.recent_events && userDetails.recent_events.length > 0 && (
              <div className="border rounded-lg p-4" data-testid="user-activity-timeline">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="font-medium flex items-center gap-2">
                    <Eye size={18} className="text-indigo-500" />
                    Detaylı Adım Takibi ({userDetails.recent_events.length})
                  </h5>
                  <span className="text-xs text-gray-400">Son {userDetails.recent_events.length} hareket</span>
                </div>
                <div className="space-y-1 max-h-96 overflow-y-auto pr-2">
                  {userDetails.recent_events.map((ev, idx) => {
                    const LABELS = {
                      page_view: { tr: 'Sayfa görüntüledi', color: 'bg-blue-100 text-blue-700' },
                      page_exit: { tr: 'Sayfadan çıktı', color: 'bg-gray-100 text-gray-700' },
                      product_view: { tr: 'Ürün inceledi', color: 'bg-purple-100 text-purple-700' },
                      product_click: { tr: 'Ürüne tıkladı', color: 'bg-indigo-100 text-indigo-700' },
                      add_to_cart: { tr: 'Sepete ekledi', color: 'bg-green-100 text-green-700' },
                      remove_from_cart: { tr: 'Sepetten çıkardı', color: 'bg-red-100 text-red-700' },
                      add_to_favorites: { tr: 'Favoriye ekledi', color: 'bg-pink-100 text-pink-700' },
                      remove_from_favorites: { tr: 'Favoriden çıkardı', color: 'bg-rose-100 text-rose-700' },
                      search: { tr: 'Arama yaptı', color: 'bg-yellow-100 text-yellow-700' },
                      filter: { tr: 'Filtre uyguladı', color: 'bg-amber-100 text-amber-700' },
                      filter_used: { tr: 'Filtre kullandı', color: 'bg-amber-100 text-amber-700' },
                      user_login: { tr: 'Giriş yaptı', color: 'bg-emerald-100 text-emerald-700' },
                      user_logout: { tr: 'Çıkış yaptı', color: 'bg-slate-100 text-slate-700' },
                      user_register: { tr: 'Kayıt oldu', color: 'bg-teal-100 text-teal-700' },
                    };
                    const info = LABELS[ev.event_type] || { tr: ev.event_type, color: 'bg-gray-100 text-gray-700' };
                    const product = ev.product_id ? getProduct(ev.product_id) : null;
                    return (
                      <div key={ev.id || idx} className="flex items-start gap-3 py-2 px-2 hover:bg-gray-50 rounded text-xs border-b border-gray-100 last:border-b-0">
                        <span className={`shrink-0 px-2 py-0.5 rounded font-medium ${info.color}`}>
                          {info.tr}
                        </span>
                        <div className="flex-1 min-w-0">
                          {ev.page && (
                            <p className="text-gray-700 truncate">
                              <span className="text-gray-400">Sayfa:</span> <code className="bg-gray-100 px-1 rounded">{ev.page}</code>
                            </p>
                          )}
                          {product && (
                            <p className="text-gray-600 truncate">
                              <span className="text-gray-400">Ürün:</span> {product.name}
                            </p>
                          )}
                          {ev.search_query && (
                            <p className="text-gray-600 truncate">
                              <span className="text-gray-400">Arama:</span> "{ev.search_query}"
                            </p>
                          )}
                          {ev.filter_type && (
                            <p className="text-gray-600 truncate">
                              <span className="text-gray-400">{ev.filter_type}:</span> {ev.filter_value}
                            </p>
                          )}
                          {ev.duration != null && (
                            <p className="text-gray-500">
                              <span className="text-gray-400">Süre:</span> {Math.round((ev.duration || 0) / 1000)}sn
                            </p>
                          )}
                          {ev.scroll_depth != null && (
                            <p className="text-gray-500">
                              <span className="text-gray-400">Kaydırma:</span> %{ev.scroll_depth}
                            </p>
                          )}
                        </div>
                        <span className="shrink-0 text-gray-400">
                          {formatDate(ev.created_at)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Registration Info */}
            <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
              <span>Kayıt: {formatDate(user.created_at)}</span>
              {user.last_activity && (
                <span>Son Aktivite: {formatDate(user.last_activity)}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [productStats, setProductStats] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stockData, setStockData] = useState([]);
  const [salesData, setSalesData] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Chart colors
  const COLORS = ['#000000', '#374151', '#6B7280', '#9CA3AF', '#D1D5DB', '#E5E7EB', '#F3F4F6'];
  const CHART_COLORS = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#f59e0b',
    success: '#10b981',
    danger: '#ef4444'
  };

  // Admin email list (you can modify this)
  const adminEmails = ['admin@valmera.com', 'hafizecemileogut@gmail.com'];

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || !adminEmails.includes(user?.email))) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate]);

  const fetchData = async () => {
    setIsRefreshing(true);
    try {
      // Fetch all data in parallel
      const [statsRes, usersRes, ordersRes, reviewsRes, popularRes, stockRes, salesRes, messagesRes] = await Promise.all([
        fetch(`${backendUrl}/api/admin/analytics/summary`),
        fetch(`${backendUrl}/api/admin/users?limit=50`),
        fetch(`${backendUrl}/api/admin/orders?limit=50`),
        fetch(`${backendUrl}/api/admin/reviews?limit=50`),
        fetch(`${backendUrl}/api/analytics/popular-products?limit=10`),
        fetch(`${backendUrl}/api/stock/all`),
        fetch(`${backendUrl}/api/admin/sales/summary`),
        fetch(`${backendUrl}/api/admin/messages`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (ordersRes.ok) setOrders(await ordersRes.json());
      if (reviewsRes.ok) setReviews(await reviewsRes.json());
      if (popularRes.ok) setProductStats(await popularRes.json());
      if (stockRes.ok) setStockData(await stockRes.json());
      if (salesRes.ok) setSalesData(await salesRes.json());
      if (messagesRes.ok) setMessages(await messagesRes.json());
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && adminEmails.includes(user?.email)) {
      fetchData();
    }
  }, [isAuthenticated, user]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDuration = (ms) => {
    if (!ms) return '0 sn';
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds} sn`;
    const minutes = Math.round(seconds / 60);
    return `${minutes} dk`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated || !adminEmails.includes(user?.email)) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-wider">YÖNETİCİ PANELİ</h1>
            <p className="text-gray-500 text-sm mt-1">Hoş geldiniz, {user?.name}</p>
          </div>
          <button
            onClick={fetchData}
            disabled={isRefreshing}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800 disabled:opacity-50"
          >
            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
            Yenile
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Users size={18} />
                <span className="text-xs">Kullanıcılar</span>
              </div>
              <p className="text-2xl font-semibold">{stats.total_users}</p>
            </div>
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <ShoppingBag size={18} />
                <span className="text-xs">Siparişler</span>
              </div>
              <p className="text-2xl font-semibold">{stats.total_orders}</p>
            </div>
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Eye size={18} />
                <span className="text-xs">Görüntüleme</span>
              </div>
              <p className="text-2xl font-semibold">{stats.total_product_views}</p>
            </div>
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Heart size={18} />
                <span className="text-xs">Favoriler</span>
              </div>
              <p className="text-2xl font-semibold">{stats.total_favorites}</p>
            </div>
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 mb-2">
                <Star size={18} />
                <span className="text-xs">Yorumlar</span>
              </div>
              <p className="text-2xl font-semibold">{stats.total_reviews}</p>
            </div>
            <div className="bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 text-orange-500 mb-2">
                <Package size={18} />
                <span className="text-xs">Bekleyen</span>
              </div>
              <p className="text-2xl font-semibold text-orange-500">{stats.pending_orders}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Genel Bakış', icon: BarChart3 },
            { id: 'users', label: 'Kullanıcılar', icon: Users },
            { id: 'activity', label: 'Canlı Aktivite', icon: Eye },
            { id: 'orders', label: 'Siparişler', icon: ShoppingBag },
            { id: 'messages', label: 'Mesajlar', icon: Mail },
            { id: 'reviews', label: 'Yorumlar', icon: MessageSquare },
            { id: 'products', label: 'Ürünler', icon: Edit },
            { id: 'collections', label: 'Koleksiyonlar', icon: Image },
            { id: 'stories', label: 'Hikayeler', icon: Film },
            { id: 'images', label: 'Ürün Görselleri', icon: Image },
            { id: 'blog', label: 'Blog', icon: Edit },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-black text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
              {tab.id === 'messages' && messages.filter(m => !m.is_read).length > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {messages.filter(m => !m.is_read).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white shadow-sm">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-6">
              {/* Sales Summary Cards */}
              {salesData && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 text-green-700 mb-2">
                      <DollarSign size={18} />
                      <span className="text-xs font-medium">Toplam Gelir</span>
                    </div>
                    <p className="text-2xl font-bold text-green-800">{formatPrice(salesData.total_revenue || 0)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 text-blue-700 mb-2">
                      <Package size={18} />
                      <span className="text-xs font-medium">Tamamlanan</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-800">{salesData.completed_orders || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg border border-yellow-200">
                    <div className="flex items-center gap-2 text-yellow-700 mb-2">
                      <Clock size={18} />
                      <span className="text-xs font-medium">Bekleyen</span>
                    </div>
                    <p className="text-2xl font-bold text-yellow-800">{salesData.pending_orders || 0}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                    <div className="flex items-center gap-2 text-purple-700 mb-2">
                      <Activity size={18} />
                      <span className="text-xs font-medium">Bugün</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-800">{salesData.orders_today || 0} sipariş</p>
                  </div>
                </div>
              )}

              {/* Charts Grid */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Product Views Chart */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Eye size={16} />
                    Ürün Görüntülemeleri
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={productStats.map(p => ({
                      name: products.find(pr => pr.id === p.product_id)?.name?.substring(0, 10) || `Ürün #${p.product_id}`,
                      views: p.view_count || 0,
                      duration: Math.round((p.avg_duration || 0) / 1000)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                        formatter={(value, name) => [value, name === 'views' ? 'Görüntüleme' : 'Ort. Süre (sn)']}
                      />
                      <Bar dataKey="views" fill="#000" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Stock Status Chart */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Package size={16} />
                    Stok Durumu
                  </h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={stockData.map(s => ({
                      name: `${products.find(p => p.id === s.product_id)?.name?.substring(0, 8) || 'Ürün'} (${s.size})`,
                      stok: s.quantity || 0,
                      rezerve: s.reserved || 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip 
                        contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                      />
                      <Legend />
                      <Bar dataKey="stok" fill="#10b981" name="Stok" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="rezerve" fill="#f59e0b" name="Rezerve" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Order Status Pie Chart */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <ShoppingBag size={16} />
                    Sipariş Durumları
                  </h3>
                  {orders.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Bekleyen', value: orders.filter(o => o.status === 'pending').length, color: '#f59e0b' },
                            { name: 'Onaylanan', value: orders.filter(o => o.status === 'confirmed').length, color: '#3b82f6' },
                            { name: 'Kargoda', value: orders.filter(o => o.status === 'shipped').length, color: '#8b5cf6' },
                            { name: 'Teslim', value: orders.filter(o => o.status === 'delivered').length, color: '#10b981' },
                            { name: 'İptal', value: orders.filter(o => o.status === 'cancelled').length, color: '#ef4444' },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {[
                            { name: 'Bekleyen', value: orders.filter(o => o.status === 'pending').length, color: '#f59e0b' },
                            { name: 'Onaylanan', value: orders.filter(o => o.status === 'confirmed').length, color: '#3b82f6' },
                            { name: 'Kargoda', value: orders.filter(o => o.status === 'shipped').length, color: '#8b5cf6' },
                            { name: 'Teslim', value: orders.filter(o => o.status === 'delivered').length, color: '#10b981' },
                            { name: 'İptal', value: orders.filter(o => o.status === 'cancelled').length, color: '#ef4444' },
                          ].filter(d => d.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      Henüz sipariş yok
                    </div>
                  )}
                </div>

                {/* User Registration Type */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
                    <Users size={16} />
                    Kullanıcı Kayıt Türleri
                  </h3>
                  {users.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Google', value: users.filter(u => u.auth_type === 'google').length, color: '#4285f4' },
                            { name: 'Email', value: users.filter(u => u.auth_type === 'email').length, color: '#000000' },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}`}
                        >
                          {[
                            { name: 'Google', value: users.filter(u => u.auth_type === 'google').length, color: '#4285f4' },
                            { name: 'Email', value: users.filter(u => u.auth_type === 'email').length, color: '#000000' },
                          ].filter(d => d.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-gray-400">
                      Henüz kullanıcı yok
                    </div>
                  )}
                </div>
              </div>

              {/* Popular Products Table */}
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <TrendingUp size={20} />
                Popüler Ürünler
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Ürün</th>
                      <th className="text-left p-3">Görüntüleme</th>
                      <th className="text-left p-3">Toplam Süre</th>
                      <th className="text-left p-3">Ort. Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productStats.map((product) => {
                      const productInfo = products.find(p => p.id === product.product_id);
                      return (
                        <tr key={product.product_id} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <div className="flex items-center gap-3">
                              {productInfo?.image && (
                                <img src={getImageUrl(productInfo.image)} alt="" className="w-10 h-10 object-cover rounded" />
                              )}
                              <span>{productInfo?.name || `Ürün #${product.product_id}`}</span>
                            </div>
                          </td>
                          <td className="p-3 font-medium">{product.view_count}</td>
                          <td className="p-3">{formatDuration(product.total_duration)}</td>
                          <td className="p-3">{formatDuration(product.avg_duration)}</td>
                        </tr>
                      );
                    })}
                    {productStats.length === 0 && (
                      <tr>
                        <td colSpan={4} className="p-4 text-center text-gray-500">
                          Henüz veri yok
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Users size={20} />
                Kayıtlı Kullanıcılar ({users.length})
              </h2>
              
              {/* User Table - More detailed view */}
              <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-4 font-medium">Kullanıcı</th>
                        <th className="text-left p-4 font-medium">İletişim</th>
                        <th className="text-left p-4 font-medium">Adres</th>
                        <th className="text-left p-4 font-medium">İstatistik</th>
                        <th className="text-left p-4 font-medium">Kayıt</th>
                        <th className="text-left p-4 font-medium">İşlem</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users.map((u) => (
                        <tr key={u.user_id} className="hover:bg-gray-50">
                          {/* User Info */}
                          <td className="p-4">
                            <div className="flex items-center gap-3">
                              {u.picture ? (
                                <img src={getImageUrl(u.picture)} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                  <Users size={16} className="text-gray-500" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-xs text-gray-500">{u.email}</p>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                                  u.auth_type === 'google' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                                }`}>
                                  {u.auth_type === 'google' ? 'Google' : 'Email'}
                                </span>
                              </div>
                            </div>
                          </td>
                          
                          {/* Contact */}
                          <td className="p-4">
                            {u.phone ? (
                              <div className="flex items-center gap-2 text-gray-700">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                </svg>
                                <span>{u.phone}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Telefon yok</span>
                            )}
                          </td>
                          
                          {/* Address */}
                          <td className="p-4 max-w-xs">
                            {(u.address || u.city || u.district) ? (
                              <div className="text-gray-700 text-xs space-y-0.5">
                                {u.address && <p className="font-medium">{u.address}</p>}
                                {(u.district || u.city) && (
                                  <p>{[u.district, u.city].filter(Boolean).join(', ')}</p>
                                )}
                                {u.postal_code && (
                                  <p className="text-gray-400">{u.postal_code}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Adres yok</span>
                            )}
                          </td>
                          
                          {/* Stats */}
                          <td className="p-4">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1 text-xs">
                                <Heart size={12} className="text-red-400" />
                                <span>{u.favorites_count || 0} favori</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs">
                                <Package size={12} className="text-blue-400" />
                                <span>{u.orders_count || 0} sipariş</span>
                              </div>
                              <div className="flex items-center gap-1 text-xs text-gray-400">
                                <Eye size={12} />
                                <span>{u.total_events || 0} aktivite</span>
                              </div>
                            </div>
                          </td>
                          
                          {/* Registration */}
                          <td className="p-4">
                            <div className="text-xs text-gray-500">
                              {formatDate(u.created_at)}
                            </div>
                            {u.last_activity && (
                              <div className="text-xs text-gray-400 mt-1">
                                Son: {formatDate(u.last_activity)}
                              </div>
                            )}
                          </td>
                          
                          {/* Actions */}
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedUser(u)}
                                className="text-blue-600 hover:text-blue-800 text-xs underline"
                              >
                                Detay
                              </button>
                              {!['admin@valmera.com', 'hafizecemileogut@gmail.com'].includes(u.email) && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm(`${u.email} kullanıcısını silmek istediğinize emin misiniz?`)) {
                                      try {
                                        const res = await fetch(`${backendUrl}/api/admin/users/${u.user_id}`, { method: 'DELETE' });
                                        if (res.ok) {
                                          fetchData();
                                          alert('Kullanıcı silindi');
                                        } else {
                                          const err = await res.json();
                                          alert(err.detail || 'Hata oluştu');
                                        }
                                      } catch (e) {
                                        alert('Hata: ' + e.message);
                                      }
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-800 text-xs"
                                >
                                  Sil
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* User Detail Modal */}
              {selectedUser && (
                <UserDetailModal 
                  user={selectedUser} 
                  onClose={() => setSelectedUser(null)} 
                  backendUrl={backendUrl}
                />
              )}
            </div>
          )}

          {/* Live Activity Tab */}
          {activeTab === 'activity' && (
            <ActivityFeedTab backendUrl={backendUrl} users={users} setSelectedUser={setSelectedUser} formatDate={formatDate} />
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && (
            <div className="p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <Mail size={20} />
                İletişim Mesajları ({messages.length})
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Messages List */}
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {messages.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">Henüz mesaj yok</p>
                  ) : (
                    messages.map((msg) => (
                      <div
                        key={msg.message_id || msg._id}
                        onClick={() => {
                          setSelectedMessage(msg);
                          // Mark as read
                          if (!msg.is_read) {
                            fetch(`${backendUrl}/api/admin/messages/${msg.message_id}/read`, { method: 'PUT' })
                              .then(() => {
                                setMessages(prev => prev.map(m => 
                                  m.message_id === msg.message_id ? { ...m, is_read: true } : m
                                ));
                              });
                          }
                        }}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedMessage?.message_id === msg.message_id 
                            ? 'border-black bg-gray-50' 
                            : 'hover:bg-gray-50'
                        } ${!msg.is_read ? 'border-l-4 border-l-blue-500' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className={`font-medium ${!msg.is_read ? 'text-black' : 'text-gray-700'}`}>
                              {msg.name || 'İsimsiz'}
                            </p>
                            <p className="text-xs text-gray-500">{msg.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-400">{formatDate(msg.created_at)}</p>
                            {!msg.is_read && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Yeni</span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          {msg.subject || 'Konu belirtilmemiş'}
                        </p>
                        <p className="text-sm text-gray-500 truncate">
                          {msg.message?.slice(0, 80)}...
                        </p>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Message Detail */}
                <div className="bg-white border rounded-lg">
                  {selectedMessage ? (
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4 pb-4 border-b">
                        <div>
                          <h3 className="font-medium text-lg">{selectedMessage.name || 'İsimsiz'}</h3>
                          <a href={`mailto:${selectedMessage.email}`} className="text-blue-600 text-sm hover:underline">
                            {selectedMessage.email}
                          </a>
                          {selectedMessage.phone && (
                            <p className="text-sm text-gray-500 mt-1">
                              Tel: {selectedMessage.phone}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">{formatDate(selectedMessage.created_at)}</p>
                          <button
                            onClick={async () => {
                              if (window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) {
                                try {
                                  const res = await fetch(`${backendUrl}/api/admin/messages/${selectedMessage.message_id}`, { method: 'DELETE' });
                                  if (res.ok) {
                                    setMessages(prev => prev.filter(m => m.message_id !== selectedMessage.message_id));
                                    setSelectedMessage(null);
                                  }
                                } catch (e) {
                                  console.error(e);
                                }
                              }
                            }}
                            className="text-red-600 text-xs hover:underline mt-2"
                          >
                            Sil
                          </button>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 mb-1">Konu</p>
                        <p className="font-medium">{selectedMessage.subject || 'Konu belirtilmemiş'}</p>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Mesaj</p>
                        <div className="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap text-sm">
                          {selectedMessage.message}
                        </div>
                      </div>
                      
                      <div className="mt-6 pt-4 border-t">
                        <a
                          href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject || 'İletişim Formu'}`}
                          className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 text-sm hover:bg-gray-800 transition-colors"
                        >
                          <Mail size={16} />
                          E-posta ile Yanıtla
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="p-12 text-center text-gray-500">
                      <Mail size={48} className="mx-auto mb-4 text-gray-300" />
                      <p>Bir mesaj seçin</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <ShoppingBag size={20} />
                Siparişler ({orders.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3">Sipariş No</th>
                      <th className="text-left p-3">Müşteri</th>
                      <th className="text-left p-3">Ürünler</th>
                      <th className="text-left p-3">Toplam</th>
                      <th className="text-left p-3">Durum</th>
                      <th className="text-left p-3">Ödeme</th>
                      <th className="text-left p-3">Tarih</th>
                      <th className="text-left p-3">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.order_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-mono text-xs">{order.order_id}</td>
                        <td className="p-3">{order.shipping_name}</td>
                        <td className="p-3">
                          {order.items?.map((item, i) => (
                            <div key={i} className="text-xs text-gray-600">
                              {item.product_name} ({item.size}) x{item.quantity}
                            </div>
                          ))}
                        </td>
                        <td className="p-3 font-medium">{formatPrice(order.total)}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded ${
                            order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                            order.status === 'shipped' ? 'bg-purple-100 text-purple-700' :
                            order.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                            order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.status === 'pending' ? 'Bekliyor' :
                             order.status === 'confirmed' ? 'Onaylandı' :
                             order.status === 'shipped' ? 'Kargoda' :
                             order.status === 'delivered' ? 'Teslim Edildi' :
                             order.status === 'cancelled' ? 'İptal' : order.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs rounded ${
                            order.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {order.payment_status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                          </span>
                        </td>
                        <td className="p-3 text-gray-500 text-xs">{formatDate(order.created_at)}</td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            {order.status === 'pending' && (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${backendUrl}/api/admin/orders/${order.order_id}/confirm`, { method: 'POST' });
                                    if (res.ok) {
                                      fetchData();
                                      alert('Sipariş onaylandı');
                                    } else {
                                      const err = await res.json();
                                      alert(err.detail || 'Hata oluştu');
                                    }
                                  } catch (e) {
                                    alert('Hata: ' + e.message);
                                  }
                                }}
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                              >
                                Onayla
                              </button>
                            )}
                            {(order.status === 'pending' || order.status === 'confirmed') && (
                              <button
                                onClick={async () => {
                                  const tracking = prompt('Kargo takip numarası (opsiyonel):');
                                  try {
                                    const res = await fetch(`${backendUrl}/api/admin/orders/${order.order_id}/ship`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ tracking_number: tracking || '' })
                                    });
                                    if (res.ok) {
                                      fetchData();
                                      alert('Sipariş kargoya verildi');
                                    } else {
                                      const err = await res.json();
                                      alert(err.detail || 'Hata oluştu');
                                    }
                                  } catch (e) {
                                    alert('Hata: ' + e.message);
                                  }
                                }}
                                className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700"
                              >
                                Kargola
                              </button>
                            )}
                            {order.status === 'shipped' && (
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await fetch(`${backendUrl}/api/admin/orders/${order.order_id}/deliver`, { method: 'POST' });
                                    if (res.ok) {
                                      fetchData();
                                      alert('Sipariş teslim edildi olarak işaretlendi');
                                    } else {
                                      const err = await res.json();
                                      alert(err.detail || 'Hata oluştu');
                                    }
                                  } catch (e) {
                                    alert('Hata: ' + e.message);
                                  }
                                }}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                              >
                                Teslim Edildi
                              </button>
                            )}
                            {order.status === 'delivered' && (
                              <span className="text-green-600 text-xs">✓ Tamamlandı</span>
                            )}
                            {order.status === 'cancelled' && (
                              <span className="text-red-600 text-xs">✗ İptal</span>
                            )}
                            {/* Tracking Number */}
                            {(order.status === 'shipped' || order.status === 'delivered') && (
                              <div className="mt-1">
                                <input
                                  type="text"
                                  placeholder="Kargo Takip No"
                                  defaultValue={order.tracking_number || ''}
                                  onBlur={async (e) => {
                                    const newTracking = e.target.value.trim();
                                    if (newTracking !== (order.tracking_number || '')) {
                                      try {
                                        const res = await fetch(`${backendUrl}/api/admin/orders/${order.order_id}/tracking?tracking_number=${encodeURIComponent(newTracking)}`, { method: 'PUT' });
                                        if (res.ok) {
                                          fetchData();
                                        }
                                      } catch (e) {
                                        console.error('Tracking update error:', e);
                                      }
                                    }
                                  }}
                                  className="w-full px-2 py-1 text-xs border rounded"
                                />
                              </div>
                            )}
                            {/* Delete Button */}
                            <button
                              onClick={async () => {
                                if (!window.confirm('Bu siparişi silmek istediğinizden emin misiniz?')) return;
                                try {
                                  const res = await fetch(`${backendUrl}/api/admin/orders/${order.order_id}`, { method: 'DELETE' });
                                  if (res.ok) {
                                    fetchData();
                                    alert('Sipariş silindi');
                                  } else {
                                    const err = await res.json();
                                    alert(err.detail || 'Hata oluştu');
                                  }
                                } catch (e) {
                                  alert('Hata: ' + e.message);
                                }
                              }}
                              className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200 mt-1"
                            >
                              Sil
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-4 text-center text-gray-500">
                          Henüz sipariş yok
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
                <MessageSquare size={20} />
                Ürün Yorumları ({reviews.length})
              </h2>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.review_id} className="border p-4 rounded">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {review.user_picture ? (
                          <img src={getImageUrl(review.user_picture)} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                            <Users size={16} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{review.user_name}</p>
                          <p className="text-xs text-gray-500">Ürün #{review.product_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            size={14}
                            fill={i < review.rating ? '#fbbf24' : 'none'}
                            className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm">{review.comment}</p>
                    {review.images && review.images.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {review.images.map((img, i) => (
                          <img key={i} src={getImageUrl(img)} alt="" className="w-16 h-16 object-cover rounded" />
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <span className="text-xs text-gray-500">{formatDate(review.created_at)}</span>
                      <span className={`px-2 py-1 text-xs rounded ${
                        review.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {review.is_approved ? 'Onaylı' : 'Bekliyor'}
                      </span>
                    </div>
                  </div>
                ))}
                {reviews.length === 0 && (
                  <p className="text-center text-gray-500 py-8">Henüz yorum yok</p>
                )}
              </div>
            </div>
          )}

          {/* Stories Tab */}
          {activeTab === 'stories' && (
            <div className="p-6">
              <StoryManager />
            </div>
          )}

          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="p-6">
              <ProductManager />
            </div>
          )}

          {/* Collections Tab */}
          {activeTab === 'collections' && (
            <CollectionManager />
          )}

          {/* Product Images Tab - Unified */}
          {activeTab === 'images' && (
            <div className="p-6">
              <UnifiedImageManager />
            </div>
          )}

          {activeTab === 'blog' && (
            <div className="p-6">
              <BlogManager />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
